import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import { 
  Save, 
  Settings, 
  Security,
  Timer,
  Info,
  Shield,
  BugReport
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import config from '../../../config';
import axios from 'axios';

const translations = {
  ar: {
    title: 'إعدادات النظام',
    subtitle: 'تكوين إعدادات النظام والأمان المتقدمة',
    systemInfo: 'معلومات النظام',
    security: 'الأمان',
    performance: 'الأداء',
    debugging: 'التصحيح',
    version: 'الإصدار',
    build: 'البناء',
    sessionTimeout: 'مهلة الجلسة (بالدقائق)',
    maxLoginAttempts: 'الحد الأقصى لمحاولات تسجيل الدخول',
    enableAuditLog: 'تفعيل سجل التدقيق',
    enableTwoFactor: 'تفعيل المصادقة الثنائية',
    debugMode: 'وضع التصحيح',
    saveChanges: 'حفظ التغييرات',
    saveSuccess: 'تم حفظ الإعدادات بنجاح',
    saveError: 'فشل حفظ الإعدادات'
  },
  en: {
    title: 'System Settings',
    subtitle: 'Configure advanced system and security settings',
    systemInfo: 'System Information',
    security: 'Security',
    performance: 'Performance',
    debugging: 'Debugging',
    version: 'Version',
    build: 'Build',
    sessionTimeout: 'Session Timeout (minutes)',
    maxLoginAttempts: 'Max Login Attempts',
    enableAuditLog: 'Enable Audit Log',
    enableTwoFactor: 'Enable Two-Factor Authentication',
    debugMode: 'Debug Mode',
    saveChanges: 'Save Changes',
    saveSuccess: 'Settings saved successfully',
    saveError: 'Failed to save settings'
  }
};

export default function SystemSettingsTab() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [formData, setFormData] = useState({
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    enableAuditLog: true,
    enableTwoFactor: false,
    debugMode: false
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_BASE_URL}/SystemSettings/bulk`, 
        Object.entries(formData).map(([key, value]) => ({
          settingKey: key,
          settingValue: value.toString(),
          category: 'System'
        })),
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setMessage({ type: 'success', text: t.saveSuccess });
    } catch (error: any) {
      setMessage({ type: 'error', text: t.saveError });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Settings sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {t.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t.subtitle}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* System Info Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <Info sx={{ color: '#667eea', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.systemInfo}
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <Box 
                sx={{ 
                  flex: 1,
                  p: 2.5, 
                  bgcolor: 'white', 
                  borderRadius: 2,
                  border: '2px solid rgba(102, 126, 234, 0.2)'
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  {t.version}
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary" mt={0.5}>
                  1.0.0
                </Typography>
                <Chip 
                  label="Stable" 
                  size="small" 
                  color="success"
                  sx={{ mt: 1, fontWeight: 600 }}
                />
              </Box>

              <Box 
                sx={{ 
                  flex: 1,
                  p: 2.5, 
                  bgcolor: 'white', 
                  borderRadius: 2,
                  border: '2px solid rgba(118, 75, 162, 0.2)'
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  {t.build}
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="secondary" mt={0.5}>
                  2025.01.03
                </Typography>
                <Chip 
                  label="Latest" 
                  size="small" 
                  sx={{ mt: 1, bgcolor: 'rgba(118, 75, 162, 0.1)', color: '#764ba2', fontWeight: 600 }}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Security Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <Security sx={{ color: '#667eea', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.security}
              </Typography>
            </Stack>

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label={t.sessionTimeout}
                  name="sessionTimeout"
                  type="number"
                  value={formData.sessionTimeout}
                  onChange={handleChange}
                  inputProps={{ min: 5, max: 120 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                <TextField
                  fullWidth
                  label={t.maxLoginAttempts}
                  name="maxLoginAttempts"
                  type="number"
                  value={formData.maxLoginAttempts}
                  onChange={handleChange}
                  inputProps={{ min: 3, max: 10 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enableAuditLog}
                      onChange={handleChange}
                      name="enableAuditLog"
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Shield sx={{ fontSize: 20, color: formData.enableAuditLog ? '#667eea' : '#bbb' }} />
                      <span>{t.enableAuditLog}</span>
                    </Stack>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enableTwoFactor}
                      onChange={handleChange}
                      name="enableTwoFactor"
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Security sx={{ fontSize: 20, color: formData.enableTwoFactor ? '#667eea' : '#bbb' }} />
                      <span>{t.enableTwoFactor}</span>
                    </Stack>
                  }
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Performance & Debugging Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            border: formData.debugMode ? '2px solid #ff9800' : '1px solid #e0e0e0',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: formData.debugMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(118, 75, 162, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <BugReport sx={{ color: formData.debugMode ? '#ff9800' : '#764ba2', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.debugging}
              </Typography>
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.debugMode}
                  onChange={handleChange}
                  name="debugMode"
                  color="warning"
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <BugReport sx={{ fontSize: 20, color: formData.debugMode ? '#ff9800' : '#bbb' }} />
                  <span>{t.debugMode}</span>
                </Stack>
              }
            />

            {formData.debugMode && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                Debug mode is active - performance may be affected
              </Alert>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            px: 5,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
            }
          }}
        >
          {t.saveChanges}
        </Button>
      </Box>
    </Box>
  );
}